
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { AlertCircle } from 'lucide-react';
import { Subscription, SubscriptionPlan } from '@/services/subscriptions';

interface CurrentSubscriptionProps {
  subscription: Subscription;
  plan: SubscriptionPlan;
  leadImports: number;
  leadQuotaPercentage: number;
}

const CurrentSubscription = ({ 
  subscription, 
  plan, 
  leadImports, 
  leadQuotaPercentage 
}: CurrentSubscriptionProps) => {
  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-xl md:text-2xl font-bold">
              Your Current Plan: {plan.name}
            </h2>
            <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
              {subscription.status === 'active' ? 'Active' : subscription.status}
            </div>
          </div>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Current billing period started on {subscription.currentPeriodStart?.toLocaleDateString() || subscription.createdAt.toLocaleDateString()}</p>
            {subscription.tier !== 'free' && subscription.currentPeriodEnd ? (
              <p className={subscription.currentPeriodEnd < new Date() ? 'text-red-600 font-medium' : ''}>
                {subscription.currentPeriodEnd < new Date() ? 'Expired on' : 'Renews on'}: {subscription.currentPeriodEnd.toLocaleDateString()}
              </p>
            ) : subscription.tier === 'free' ? (
              <p className="text-muted-foreground">No expiration - Free plan active indefinitely</p>
            ) : null}
          </div>
        </div>
      </div>
      
      {plan.leadLimit !== Infinity && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="font-medium">Lead imports this month</span>
            <span className="font-bold">{leadImports} / {plan.leadLimit}</span>
          </div>
          <div className="h-2 bg-white/80 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${leadQuotaPercentage}%` }}
            />
          </div>
        </div>
      )}
      
      {plan.leadLimit === Infinity && (
        <div className="text-sm">
          <span className="font-medium">Lead imports this month: </span>
          <span className="font-bold">{leadImports} / Unlimited</span>
        </div>
      )}
        
        {leadImports >= plan.leadLimit && (
          <div className="bg-yellow-100 text-yellow-800 p-3 rounded-md flex items-start mt-2">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">You've reached your monthly lead limit</p>
              <p className="text-sm">Consider upgrading your plan to import more leads this month.</p>
            </div>
          </div>
        )}
        
        {subscription.currentPeriodEnd && subscription.tier !== 'free' && (
          (() => {
            const daysUntilExpiry = Math.ceil(
              (subscription.currentPeriodEnd.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
            );
            
            if (daysUntilExpiry < 0) {
              return (
                <div className="bg-red-100 text-red-800 p-3 rounded-md flex items-start mt-2">
                  <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Your subscription has expired</p>
                    <p className="text-sm">Renew now to continue accessing premium features.</p>
                  </div>
                </div>
              );
            } else if (daysUntilExpiry <= 7) {
              return (
                <div className="bg-yellow-100 text-yellow-800 p-3 rounded-md flex items-start mt-2">
                  <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Your subscription expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}</p>
                    <p className="text-sm">Renew now to avoid interruption of service.</p>
                  </div>
                </div>
              );
            }
            return null;
          })()
        )}
    </div>
  );
};

export default CurrentSubscription;
